import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Filter, 
  X, 
  Search,
  Clock,
  CheckCircle,
  Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import EngineCard from '@/components/EngineCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Condition badge colors
const conditionColors = {
  'New': 'bg-emerald-500',
  'Like New': 'bg-sky-500',
  'Rebuilt': 'bg-blue-500',
  'Good Used': 'bg-amber-500',
  'High Hour': 'bg-orange-500',
};

export default function InventoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [brands, setBrands] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    brand: searchParams.get('brand') || '',
    condition: searchParams.get('condition') || '',
    tier_rating: searchParams.get('tier_rating') || '',
    min_hp: parseInt(searchParams.get('min_hp')) || 0,
    max_hp: parseInt(searchParams.get('max_hp')) || 10000,
    is_new: searchParams.get('is_new') === 'true',
    is_ready_to_ship: searchParams.get('is_ready_to_ship') === 'true',
    sort_by: searchParams.get('sort_by') || 'horsepower',
    sort_order: searchParams.get('sort_order') || 'desc',
    page: parseInt(searchParams.get('page')) || 1
  });

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    fetchEngines();
    updateURLParams();
  }, [filters]);

  const fetchFilters = async () => {
    try {
      const [brandsRes, conditionsRes, tiersRes] = await Promise.all([
        axios.get(`${API_URL}/api/engines/brands`),
        axios.get(`${API_URL}/api/engines/conditions`),
        axios.get(`${API_URL}/api/engines/tiers`)
      ]);
      setBrands(brandsRes.data);
      setConditions(conditionsRes.data);
      setTiers(tiersRes.data);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  const fetchEngines = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.tier_rating) params.append('tier_rating', filters.tier_rating);
      if (filters.min_hp > 0) params.append('min_hp', filters.min_hp);
      if (filters.max_hp < 10000) params.append('max_hp', filters.max_hp);
      if (filters.is_new) params.append('is_new', 'true');
      if (filters.is_ready_to_ship) params.append('is_ready_to_ship', 'true');
      params.append('sort_by', filters.sort_by);
      params.append('sort_order', filters.sort_order);
      params.append('page', filters.page);
      params.append('limit', 12);

      const response = await axios.get(`${API_URL}/api/engines?${params}`);
      setEngines(response.data.engines);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch engines:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateURLParams = () => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.brand) params.set('brand', filters.brand);
    if (filters.condition) params.set('condition', filters.condition);
    if (filters.tier_rating) params.set('tier_rating', filters.tier_rating);
    if (filters.min_hp > 0) params.set('min_hp', filters.min_hp);
    if (filters.max_hp < 10000) params.set('max_hp', filters.max_hp);
    if (filters.is_new) params.set('is_new', 'true');
    if (filters.is_ready_to_ship) params.set('is_ready_to_ship', 'true');
    if (filters.sort_by !== 'horsepower') params.set('sort_by', filters.sort_by);
    if (filters.sort_order !== 'desc') params.set('sort_order', filters.sort_order);
    if (filters.page > 1) params.set('page', filters.page);
    setSearchParams(params);
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      brand: '',
      condition: '',
      tier_rating: '',
      min_hp: 0,
      max_hp: 10000,
      is_new: false,
      is_ready_to_ship: false,
      sort_by: 'horsepower',
      sort_order: 'desc',
      page: 1
    });
  };

  const activeFilterCount = [
    filters.brand,
    filters.condition,
    filters.tier_rating,
    filters.min_hp > 0,
    filters.max_hp < 10000,
    filters.is_new,
    filters.is_ready_to_ship
  ].filter(Boolean).length;

  const FilterSidebar = () => (
    <div className="space-y-6">
      {/* Search */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Search</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search by model, item #..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-10"
            data-testid="inventory-search"
          />
        </div>
      </div>

      {/* Brand */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Brand</Label>
        <Select value={filters.brand || "all"} onValueChange={(v) => updateFilter('brand', v === "all" ? "" : v)}>
          <SelectTrigger data-testid="brand-filter">
            <SelectValue placeholder="All Brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map((brand) => (
              <SelectItem key={brand} value={brand}>{brand}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Condition */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Condition</Label>
        <Select value={filters.condition || "all"} onValueChange={(v) => updateFilter('condition', v === "all" ? "" : v)}>
          <SelectTrigger data-testid="condition-filter">
            <SelectValue placeholder="All Conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Conditions</SelectItem>
            {conditions.map((condition) => (
              <SelectItem key={condition} value={condition}>{condition}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tier Rating */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">Tier Rating</Label>
        <Select value={filters.tier_rating || "all"} onValueChange={(v) => updateFilter('tier_rating', v === "all" ? "" : v)}>
          <SelectTrigger data-testid="tier-filter">
            <SelectValue placeholder="All Tiers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tiers</SelectItem>
            {tiers.map((tier) => (
              <SelectItem key={tier} value={tier}>{tier}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Horsepower Range */}
      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-3 block">
          Horsepower: {filters.min_hp.toLocaleString()} - {filters.max_hp.toLocaleString()} HP
        </Label>
        <Slider
          min={0}
          max={10000}
          step={100}
          value={[filters.min_hp, filters.max_hp]}
          onValueChange={([min, max]) => {
            setFilters(prev => ({ ...prev, min_hp: min, max_hp: max, page: 1 }));
          }}
          className="mt-4"
          data-testid="hp-slider"
        />
      </div>

      {/* Status Filters */}
      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-900 block">Availability</Label>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="new-only" 
            checked={filters.is_new}
            onCheckedChange={(checked) => updateFilter('is_new', checked)}
            data-testid="new-filter"
          />
          <label htmlFor="new-only" className="text-sm text-slate-600 cursor-pointer flex items-center gap-1">
            <CheckCircle className="w-4 h-4 text-emerald-500" /> New Only
          </label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox 
            id="ready-to-ship" 
            checked={filters.is_ready_to_ship}
            onCheckedChange={(checked) => updateFilter('is_ready_to_ship', checked)}
            data-testid="ready-filter"
          />
          <label htmlFor="ready-to-ship" className="text-sm text-slate-600 cursor-pointer flex items-center gap-1">
            <Package className="w-4 h-4 text-blue-500" /> Ready to Ship
          </label>
        </div>
      </div>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button variant="outline" onClick={clearFilters} className="w-full" data-testid="clear-filters">
          Clear All Filters
        </Button>
      )}
    </div>
  );

  return (
    <main className="min-h-screen bg-slate-50" data-testid="inventory-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="container-custom py-12">
          <h1 className="text-4xl font-bold mb-2">Marine Engine Inventory</h1>
          <p className="text-slate-400 text-lg">
            {total} diesel engines available from trusted manufacturers
          </p>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 bg-white p-6 rounded-sm border border-slate-200 max-h-[calc(100vh-120px)] overflow-y-auto">
              <h2 className="text-lg font-semibold text-slate-900 mb-6">Filter Inventory</h2>
              <FilterSidebar />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6 flex flex-wrap items-center gap-4">
              {/* Mobile Filter Toggle */}
              <Button 
                variant="outline" 
                className="lg:hidden"
                onClick={() => setMobileFiltersOpen(true)}
                data-testid="mobile-filter-toggle"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {activeFilterCount > 0 && (
                  <Badge className="ml-2 bg-orange-500">{activeFilterCount}</Badge>
                )}
              </Button>

              {/* Active Filters */}
              <div className="flex-1 flex flex-wrap gap-2">
                {filters.brand && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.brand}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter('brand', '')} />
                  </Badge>
                )}
                {filters.condition && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.condition}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter('condition', '')} />
                  </Badge>
                )}
                {filters.tier_rating && (
                  <Badge variant="secondary" className="gap-1">
                    {filters.tier_rating}
                    <X className="w-3 h-3 cursor-pointer" onClick={() => updateFilter('tier_rating', '')} />
                  </Badge>
                )}
              </div>

              {/* Sort */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600 hidden sm:block">Sort:</span>
                <Select 
                  value={`${filters.sort_by}-${filters.sort_order}`} 
                  onValueChange={(v) => {
                    const [sortBy, sortOrder] = v.split('-');
                    setFilters(prev => ({ ...prev, sort_by: sortBy, sort_order: sortOrder, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-48" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="horsepower-desc">HP: High to Low</SelectItem>
                    <SelectItem value="horsepower-asc">HP: Low to High</SelectItem>
                    <SelectItem value="price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="price-desc">Price: High to Low</SelectItem>
                    <SelectItem value="created_at-desc">Newest Listed</SelectItem>
                    <SelectItem value="views-desc">Most Viewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Results */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-sm border border-slate-200 animate-pulse">
                    <div className="aspect-video bg-slate-200" />
                    <div className="p-6 space-y-3">
                      <div className="h-4 bg-slate-200 rounded w-1/3" />
                      <div className="h-6 bg-slate-200 rounded w-3/4" />
                      <div className="h-4 bg-slate-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : engines.length === 0 ? (
              <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                <p className="text-slate-600 mb-4">No engines found matching your criteria</p>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="engine-grid">
                  {engines.map((engine) => (
                    <EngineCard key={engine.id} engine={engine} showCondition={true} />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center gap-2">
                    <Button
                      variant="outline"
                      disabled={filters.page === 1}
                      onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        const pageNum = filters.page <= 3 ? i + 1 : filters.page - 2 + i;
                        if (pageNum > totalPages) return null;
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === filters.page ? "default" : "outline"}
                            onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                            className="w-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      disabled={filters.page === totalPages}
                      onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Filter Inventory</h2>
              <Button variant="ghost" size="icon" onClick={() => setMobileFiltersOpen(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <FilterSidebar />
            <Button className="w-full mt-6 btn-primary" onClick={() => setMobileFiltersOpen(false)}>
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}
